import React, {useContext} from "react";
import { makeStyles } from "@material-ui/core/styles";
import {Box, Collapse, colors, IconButton, Typography} from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import {NotificationContext, NotificationDispatchContext} from "../../../context/NotificationContext";

export type NotificationType = "error" | "success" | "info";

interface GenericWrapperProps {
  type: NotificationType;
  content: string;
  onClose?: () => void
}

const computeBackgroundColor = (type: NotificationType) => {
  if(type === "error") {
    return "#FCEAEA";
  }

  if(type === "success") {
    return "#D7F5E0";
  }

  return "#fafafa";
};

const computeTextColor = (type: NotificationType) => {
  if(type === "error") {
    return colors.red[800];
  }

  if(type === "success") {
    return colors.green[800];
  }

  return colors.grey[800];
};

const computeBorderColor = (type: NotificationType) => {
  if(type === "error") {
    return colors.red[100];
  }

  if(type === "success") {
    return colors.green[100];
  }

  return colors.grey[200];
};

const computeIcon = (type: NotificationType) => {
  if(type === "error") {
    return <CheckCircleOutlineIcon fontSize="small" />
  }

  if(type === "success") {
    return <ErrorOutlineIcon fontSize="small" />
  }

  return <HelpOutlineIcon fontSize="small" />
};

const useStyles = makeStyles((theme) => ({
  wrapper: {
    padding: "4px 20px 2px 26px",
    borderBottom: (props: GenericWrapperProps) => `1px solid ${computeBorderColor(props.type)}`,
    borderTop: (props: GenericWrapperProps) => `1px solid ${computeBorderColor(props.type)}`,
    backgroundColor: (props: GenericWrapperProps) => `${computeBackgroundColor(props.type)}`,

    display: "flex",
    alignItems: "start",

    "& .MuiIconButton-root .MuiIconButton-label .MuiSvgIcon": {
      paddingTop: "12px",
    },
    "& .MuiIconButton-root .MuiIconButton-label .MuiSvgIcon-fontSizeSmall": {
      fontSize: "0.88rem !important"
    }
  },
  content: {
    fontSize: "0.84rem",
    fontWeight: theme.typography.fontWeightRegular,
    paddingTop: "12px",
    color: (props: GenericWrapperProps) => `${computeTextColor(props.type)}`,
  },
  indicatorIcon: {
    "& .MuiSvgIcon-fontSizeSmall": {
      fontSize: "1.4rem !important",
      color: (props: GenericWrapperProps) => `${computeTextColor(props.type)} !important`,
      marginTop: "10px",
    }
  }
}), {
  name: "generic-error-wrapper-styles",
  flip: null,
});


export const GenericWrapper = (props: GenericWrapperProps) => {
  const classes = useStyles(props);

  return (
      <Box className={classes.wrapper}>
        <Box component="div" style={{flexGrow: 1}} display="flex" alignItems="start">
          <Box component="p" mr={2} className={classes.indicatorIcon}>
            {computeIcon(props.type)}
          </Box>
          <Typography component="p" className={classes.content}>
            {props.content}
          </Typography>
        </Box>
        <IconButton aria-label="delete" onClick={(_) => props.onClose()}>
          <CloseIcon fontSize="small" color="inherit" />
        </IconButton>
      </Box>
  );
};

export const NotificationWrapper = () => {
  // Context
  const context = useContext(NotificationContext);
  const dispatch = useContext(NotificationDispatchContext);

  const onCloseCallback = () => {
    dispatch({
      data: {
        message: null
      }
    });
  };

  return (
      <Collapse in={context.message !== null && context.message !== undefined} timeout="auto" unmountOnExit>
        <GenericWrapper
            type={context.type}
            content={context.message !== null && context.message !== undefined ? context.message : ""}
            onClose={onCloseCallback}
        />
      </Collapse>
  );
};
